import { Input } from '@southfields-digital/mpxlive-components';

import { DataControllerInput } from 'src/components/DataController';
import { extractValueFromSuggestion } from 'src/components/DataController/utils';
import { getRepeaterItems, getValues } from 'src/components/GraphicPropertiesForm/utils';
import TimerInput from 'src/components/InputTimer';
import Repeater, { Id, Items } from 'src/components/Repeater';
import Select from 'src/components/Select';
import { toDatetimeLocal } from 'src/utils';

import InputColor from '../InputColor';

type PropertyFieldProps = {
  field: WithValue;
  onChange: (value: string | number | PropertyValue[]) => void;
  onSelectMedia?: (
    properties: ImageProperties,
    handleSelectValue?: (value: string) => void
  ) => void;
};

export default function PropertyField({
  field,
  onChange,
  onSelectMedia,
}: PropertyFieldProps): React.ReactNode {
  const { id, name, type, value } = field;

  switch (type) {
    case 'text':
      return (
        <DataControllerInput
          value={typeof value === 'string' ? value : ''}
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
          dataSources={['matchControl']}
          placeholder={field.placeholder || name}
          valueExtractor={extractValueFromSuggestion}
        />
      );
    case 'url':
      return (
        <Input
          type="url"
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder}
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
        />
      );
    case 'color':
      return <InputColor value={typeof value === 'string' ? value : ''} onChange={onChange} />;
    case 'number':
      return (
        <Input
          type="number"
          value={
            typeof field.value === 'string' || typeof field.value === 'number'
              ? String(field.value)
              : ''
          }
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
        />
      );
    case 'percentage':
      return (
        <Input
          type="number"
          min={0}
          max={100}
          suffix="%"
          value={typeof value === 'string' || typeof value === 'number' ? value : ''}
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
        />
      );
    case 'select':
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          options={field.options}
          onChange={(e: React.SyntheticEvent<HTMLSelectElement>) => onChange(e.currentTarget.value)}
        />
      );
    case 'image':
      return (
        <Input
          type="selectableInput"
          buttonIcon="media"
          value={typeof value === 'string' ? value : ''}
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
          onClick={() => onSelectMedia?.(field, (v) => onChange(v))}
        />
      );
    case 'repeater':
      return (
        <Repeater
          id={id}
          items={getRepeaterItems(field, Array.isArray(value) ? value : undefined)}
          onChangeRows={(_id: Id, _rowIndex: number, rows: Items[]) => onChange(getValues(rows))}
          handleSelectMedia={onSelectMedia}
        />
      );
    case 'countdown': {
      const inputValue =
        typeof value === 'string' && value ? toDatetimeLocal(new Date(value)).localDateTime : '';
      return (
        <Input
          type="datetime-local"
          id={id}
          name={name}
          label={name}
          value={inputValue}
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
            const v = e.currentTarget.value;
            onChange(
              typeof v === 'string' && !isNaN(Date.parse(v)) ? new Date(v).toISOString() : ''
            );
          }}
        />
      );
    }
    case 'timer':
      return (
        <TimerInput
          id={id}
          name={name}
          value={typeof value === 'number' ? value : 0}
          onChange={(v: number) => onChange(v)}
        />
      );
    default:
      return null;
  }
}
